import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { productEffectivePrice } from '../productPricing';
import { localItemPayload, wishlistApiPost } from './wishlistApi';
import { trackAddToWishlist } from '../analytics';

const KEY = 'saloni_wishlist_v1';
const WishlistContext = createContext(null);

function readLocalWishlist() {
  try {
    const raw = localStorage.getItem(KEY);
    const p = JSON.parse(raw || '[]');
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function writeLocalWishlist(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* ignore */
  }
}

function itemFromProduct(product, categorySlug) {
  const productId = String(product?.id || '');
  const cat = String(categorySlug || '');
  if (!productId || !cat) return null;
  return {
    categorySlug: cat,
    productId,
    name: String(product?.name || ''),
    image: String(product?.image || ''),
    price: productEffectivePrice(product),
  };
}

function rowsToItems(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    categorySlug: String(row.category_slug || ''),
    productId: String(row.product_external_id || ''),
    name: '',
    image: '',
    price: 0,
  }));
}

function mergeItemMeta(items, product, categorySlug) {
  const next = itemFromProduct(product, categorySlug);
  if (!next) return items;
  return items.map((x) =>
    x.categorySlug === next.categorySlug && x.productId === next.productId
      ? { ...x, name: next.name, image: next.image, price: next.price }
      : x
  );
}

export function WishlistProvider({ children }) {
  const { ready, isAuthed, token } = useAuth();
  const [items, setItems] = useState(() => readLocalWishlist());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverEnabled, setServerEnabled] = useState(true);
  const syncedTokenRef = useRef('');

  const persistLocal = useCallback((next) => {
    setItems(next);
    if (!isAuthed) writeLocalWishlist(next);
  }, [isAuthed]);

  const loadFromServer = useCallback(async (mergeLocal = false) => {
    if (!isAuthed) {
      setItems(readLocalWishlist());
      return;
    }
    setLoading(true);
    try {
      const local = mergeLocal ? readLocalWishlist() : [];
      if (mergeLocal && local.length > 0) {
        await wishlistApiPost('sync_wishlist', {
          items: local.map(localItemPayload),
        });
        writeLocalWishlist([]);
      }
      const res = await wishlistApiPost('list_wishlist');
      if (!res.ok) {
        if (mergeLocal) setItems(local);
        return;
      }
      setServerEnabled(res.data?.enabled !== false);
      const rows = res.data?.items;
      const fromServer = rowsToItems(rows);
      const enriched = local.length && !mergeLocal
        ? fromServer
        : fromServer.map((row) => {
            const hit = local.find(
              (l) => l.categorySlug === row.categorySlug && l.productId === row.productId
            );
            return hit ? { ...row, name: hit.name, image: hit.image, price: hit.price } : row;
          });
      setItems(enriched);
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) {
      syncedTokenRef.current = '';
      setItems(readLocalWishlist());
      return;
    }
    const tok = String(token || '').trim();
    if (syncedTokenRef.current === tok) return;
    syncedTokenRef.current = tok;
    void loadFromServer(true);
  }, [ready, isAuthed, token, loadFromServer]);

  const isWishlisted = useCallback(
    (categorySlug, productId) =>
      items.some((x) => x.categorySlug === categorySlug && x.productId === productId),
    [items]
  );

  const toggle = useCallback(
    async (product, categorySlug) => {
      const entry = itemFromProduct(product, categorySlug);
      if (!entry) return;

      const exists = items.some(
        (x) => x.categorySlug === entry.categorySlug && x.productId === entry.productId
      );

      if (!isAuthed) {
        persistLocal(
          exists
            ? items.filter(
                (x) => !(x.categorySlug === entry.categorySlug && x.productId === entry.productId)
              )
            : [...items, entry]
        );
        if (!exists) trackAddToWishlist({ product, categorySlug, price: entry.price });
        return;
      }

      setBusy(true);
      try {
        const action = exists ? 'remove_wishlist' : 'add_wishlist';
        const res = await wishlistApiPost(action, {
          category_slug: entry.categorySlug,
          product_external_id: entry.productId,
        });
        if (!res.ok) {
          window.alert(res.error || 'Could not update saved items.');
          return;
        }
        let next = rowsToItems(res.data?.items);
        next = mergeItemMeta(next, product, categorySlug);
        setItems(next);
        if (!exists) trackAddToWishlist({ product, categorySlug, price: entry.price });
      } finally {
        setBusy(false);
      }
    },
    [items, isAuthed, persistLocal]
  );

  const value = useMemo(
    () => ({
      items,
      count: items.length,
      loading,
      busy,
      serverEnabled,
      isAuthed,
      isWishlisted,
      toggle,
      reload: () => loadFromServer(false),
    }),
    [items, loading, busy, serverEnabled, isAuthed, isWishlisted, toggle, loadFromServer]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
